import { useEffect, useRef } from "react";
import L from "leaflet";
import { request } from "./lib/api";
import { rememberOfflinePlaces } from "./lib/offlinePlaces";
import { reportLabels } from "./lib/reports";
import { cameraLabel } from "./lib/cameraAlerts";
import type {
  LatLng,
  NearbyPlace,
  Place,
  RoadReport,
  Route,
  CameraSite,
} from "./lib/types";

export type MapHandle = {
  zoom: (delta: number) => void;
  locate: (position: LatLng) => void;
  center: () => LatLng;
};
type Props = {
  showCycleways?: boolean;
  transitStops?: import("./TransitPanel").TransitStop[];
  position: LatLng | null;
  origin: LatLng;
  destination: Place | null;
  route: Route | null;
  reports: RoadReport[];
  cameras: CameraSite[];
  onCamera: (camera: CameraSite) => void;
  nearbyPlaces: NearbyPlace[];
  onPlace: (place: Place) => void;
  follow: boolean;
  tileUrl: string;
  onPick: (p: LatLng) => void;
  onReport: (r: RoadReport) => void;
  onReady: (h: MapHandle) => void;
  onTileError: () => void;
};
const symbols: Record<string, string> = {
  POLICE: "♟",
  CAMERA: "▣",
  FIXED_CAMERA: "▣",
  MOBILE_CAMERA: "◉",
  SLIPPERY_ROAD: "≈",
  OBJECT_ON_ROAD: "◆",
  HAZARD: "!",
  ACCIDENT: "×",
  TRAFFIC: "≋",
  CLOSURE: "−",
};
function markerIcon(html: string, className: string) {
  return L.divIcon({
    className: "custom-marker",
    html: `<div class="${className}">${html}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}
export default function MapView(props: Props) {
  const el = useRef<HTMLDivElement>(null),
    map = useRef<L.Map | null>(null),
    markers = useRef<L.LayerGroup | null>(null),
    places = useRef<L.LayerGroup | null>(null),
    cameras = useRef<L.LayerGroup | null>(null),
    lines = useRef<L.LayerGroup | null>(null),
    user = useRef<L.Marker | null>(null),
    tile = useRef<L.TileLayer | null>(null);
  const callbacks = useRef(props);
  callbacks.current = props;
  useEffect(() => {
    const current = map.current;
    if (!current || !props.transitStops?.length) return;
    const group = L.layerGroup().addTo(current);
    for (const stop of new Map(props.transitStops.map(s => [s.id, s])).values()) {
      const popup = document.createElement("div");
      popup.textContent = `${stop.name} · תחנה ${stop.code}`;
      L.marker([stop.lat,stop.lng], {icon:L.divIcon({className:"transit-marker",html:"▣",iconSize:[22,24]})})
        .bindPopup(popup).addTo(group);
    }
    return () => { group.remove(); };
  }, [props.transitStops]);
  useEffect(() => {
    if (!el.current) return;
    const m = L.map(el.current, {
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true,
    }).setView([32.084, 34.783], 14);
    map.current = m;
    tile.current = L.tileLayer(props.tileUrl, {
      maxZoom: 19,
    }).addTo(m);
    tile.current.on("tileerror", () => callbacks.current.onTileError());
    markers.current = L.layerGroup().addTo(m);
    places.current = L.layerGroup().addTo(m);
    cameras.current = L.layerGroup().addTo(m);
    lines.current = L.layerGroup().addTo(m);
    m.on("click", (e: L.LeafletMouseEvent) =>
      callbacks.current.onPick({ lat: e.latlng.lat, lng: e.latlng.lng }),
    );
    callbacks.current.onReady({
      zoom: (d) => m.setZoom(m.getZoom() + d),
      locate: (p) => {
        const mobile = window.matchMedia("(max-width:800px)").matches;
        if (mobile && !callbacks.current.follow) {
          m.flyToBounds(
            L.latLngBounds([
              [p.lat, p.lng],
              [p.lat, p.lng],
            ]),
            {
              maxZoom: 16,
              duration: 0.65,
              paddingTopLeft: [35, 100],
              paddingBottomRight: [35, m.getSize().y * 0.46 + 30],
            },
          );
        } else m.flyTo([p.lat, p.lng], 16, { duration: 0.65 });
      },
      center: () => {
        const p = m.getCenter();
        return { lat: p.lat, lng: p.lng };
      },
    });
    const communityLayer=L.layerGroup().addTo(m);
    let communityTimer:ReturnType<typeof setTimeout> | undefined;
    let communityRequest:AbortController | undefined;
    let lastCenter:L.LatLng | undefined;
    let fetchedAt=0;
    function loadCommunity() {
      clearTimeout(communityTimer);
      communityTimer=setTimeout(async()=>{
        if(m.getZoom()<13){communityLayer.clearLayers();lastCenter=undefined;return;}
        const center=m.getCenter();
        if(lastCenter && center.distanceTo(lastCenter)<1500 && Date.now()-fetchedAt<60000)return;
        communityRequest?.abort();const controller=new AbortController();communityRequest=controller;
        try {
          const result=await request<{places:Place[]}>(`/api/places/community?lat=${center.lat}&lng=${center.lng}`,{},controller.signal);
          void request<{places:Place[]}>(`/api/offline/places?lat=${center.lat}&lng=${center.lng}`,{},controller.signal)
            .then(data=>rememberOfflinePlaces(data.places)).catch(()=>{});
          if(controller.signal.aborted)return;
          communityLayer.clearLayers();lastCenter=center;fetchedAt=Date.now();
          result.places.slice(0,30).forEach(place=>{
            const label=document.createElement("span");label.textContent=place.name;
            L.marker([place.lat,place.lng],{icon:markerIcon("◇","community-place-marker"),title:place.name,alt:place.name})
              .bindTooltip(label,{direction:"top"}).addTo(communityLayer)
              .on("click",event=>{L.DomEvent.stopPropagation(event);callbacks.current.onPlace(place);});
          });
        } catch { /* A temporary POI failure must not interrupt map navigation. */ }
      },800);
    }
    m.on("moveend",loadCommunity);loadCommunity();
    const observer = new ResizeObserver(() => m.invalidateSize());
    observer.observe(el.current);
    return () => {
      clearTimeout(communityTimer);communityRequest?.abort();m.off("moveend",loadCommunity);
      observer.disconnect();
      m.remove();
      map.current = null;
    };
  }, []);
  useEffect(() => {
    tile.current?.setUrl(props.showCycleways ? "/maps/cycling-20261010/{z}/{x}/{y}.png" : props.tileUrl);
  }, [props.tileUrl, props.showCycleways]);
  useEffect(() => {
    if (!cameras.current) return;
    cameras.current.clearLayers();
    for (const camera of props.cameras) {
      const label = document.createElement("span");
      label.textContent = `${cameraLabel(camera)} · ${camera.description} · ${camera.sourceStatus}`;
      const inactive = camera.status === "INACTIVE" || camera.kind === "DUMMY";
      const headings = [camera.heading, camera.secondaryHeading].filter(
        (h): h is number => h !== null && Number.isFinite(h),
      );
      const arrows = headings
        .map(
          (h) =>
            `<span class="catalog-bearing" style="transform:rotate(${h}deg)">▴</span>`,
        )
        .join("");
      const glyph =
        '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M14 4h-4L8 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-4z"/><circle cx="12" cy="13" r="4"/></svg>';
      const marker = L.marker([camera.lat, camera.lng], {
        title: `${cameraLabel(camera)}: ${camera.description}`,
        icon: markerIcon(
          glyph + arrows,
          `catalog-pin ${inactive ? "inactive" : camera.kind.toLowerCase()}`,
        ),
        zIndexOffset: inactive ? -20 : 20,
      })
        .addTo(cameras.current)
        .bindTooltip(label, { direction: "top", offset: [0, -20] })
        .on("click", (event) => {
          L.DomEvent.stopPropagation(event);
          callbacks.current.onCamera(camera);
        });
      marker
        .getElement()
        ?.setAttribute("aria-label", label.textContent || camera.name);
    }
  }, [props.cameras]);
  useEffect(() => {
    if (!places.current) return;
    places.current.clearLayers();
    const icons = {
      parking: "P",
      shelter: "◇",
      fuel: "⛽",
      food: "☕",
      culture: "▥",
      nature: "♧",
      attractions: "✦",
    };
    props.nearbyPlaces.forEach((place) => {
      const label = document.createElement("span");
      label.textContent = place.name;
      L.marker([place.lat, place.lng], {
        icon: markerIcon(icons[place.category] || "•", "poi-pin"),
      })
        .addTo(places.current!)
        .bindTooltip(label, { direction: "top", offset: [0, -18] })
        .on("click", (event) => {
          L.DomEvent.stopPropagation(event);
          callbacks.current.onPlace(place);
        });
    });
  }, [props.nearbyPlaces]);
  useEffect(() => {
    if (!map.current || !lines.current) return;
    lines.current.clearLayers();
    if (props.route) {
      const coords = props.route.geometry.map(
        (p) => [p[1], p[0]] as L.LatLngTuple,
      );
      L.polyline(coords, {
        color: "#fff",
        weight: 12,
        opacity: 0.96,
        lineJoin: "round",
      }).addTo(lines.current);
      L.polyline(coords, {
        color: "#087f78",
        weight: 7,
        opacity: 1,
        lineJoin: "round",
      }).addTo(lines.current);
      if (coords.length > 1) {
        const mobile = window.matchMedia("(max-width:800px)").matches;
        map.current.fitBounds(L.latLngBounds(coords), {
          paddingTopLeft: mobile ? [35, 100] : [65, 90],
          paddingBottomRight: mobile
            ? [35, map.current.getSize().y * 0.46 + 30]
            : [65, 90],
          maxZoom: 16,
        });
      }
    }
  }, [props.route]);
  useEffect(() => {
    if (!markers.current) return;
    markers.current.clearLayers();
    if (props.destination) {
      const label = document.createElement("span");
      label.textContent = props.destination.name;
      L.marker([props.destination.lat, props.destination.lng], {
        icon: markerIcon("<span>⚑</span>", "destination-pin"),
      })
        .addTo(markers.current)
        .bindTooltip(label, { direction: "top", offset: [0, -19] });
    }
    if (props.route)
      L.circleMarker([props.origin.lat, props.origin.lng], {
        radius: 7,
        color: "#fff",
        weight: 3,
        fillColor: "#087f78",
        fillOpacity: 1,
      }).addTo(markers.current);
    props.reports.forEach((r) =>
      L.marker([r.lat, r.lng], {
        title: reportLabels[r.type],
        icon: markerIcon(
          symbols[r.type] || "!",
          "report-pin " + r.type.toLowerCase(),
        ),
      })
        .addTo(markers.current!)
        .on("click", (e) => {
          L.DomEvent.stopPropagation(e);
          callbacks.current.onReport(r);
        }),
    );
  }, [props.reports, props.destination, props.origin, props.route]);
  useEffect(() => {
    if (!map.current) return;
    if (!props.position) {
      user.current?.remove();
      user.current = null;
      return;
    }
    const p: L.LatLngTuple = [props.position.lat, props.position.lng];
    if (user.current) user.current.setLatLng(p);
    else
      user.current = L.marker(p, {
        icon: markerIcon("<span>➤</span>", "position-pin"),
        zIndexOffset: 1000,
      }).addTo(map.current);
    if (props.follow) map.current.panTo(p, { animate: true, duration: 0.5 });
  }, [props.position, props.follow]);
  return (
    <div
      ref={el}
      className="map-canvas"
      aria-label="מפת Desty אינטראקטיבית"
    />
  );
}
